import { useEffect, useState } from "react";
import {
  MessageCircle, Search, FileText, Download, MapPin, User, ShieldAlert, ExternalLink, ShieldOff,
} from "lucide-react";
import { api, mensajeError } from "../api/client";
import { Spinner } from "../components/ui/Spinner";
import { useAuth } from "../context/AuthContext";

type TipoMensaje = "TEXTO" | "IMAGEN" | "AUDIO" | "VIDEO" | "DOCUMENTO" | "UBICACION" | "OTRO";
type Direccion = "ENTRANTE" | "SALIENTE";

interface Conversacion {
  id: string;
  telefono: string;
  nombrePerfil?: string | null;
  ultimoMensaje?: string | null;
  ultimoMensajeEn?: string | null;
  pendienteRespuesta: boolean;
  sinLeer: boolean;
}

interface Mensaje {
  id: string;
  direccion: Direccion;
  tipo: TipoMensaje;
  texto?: string | null;
  mediaId?: string | null;
  mediaMimeType?: string | null;
  createdAt: string;
}

function horaCorta(fecha: string): string {
  return new Date(fecha).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
}

function fechaCorta(fecha: string): string {
  const d = new Date(fecha);
  const hoy = new Date();
  if (d.toDateString() === hoy.toDateString()) return horaCorta(fecha);
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

export default function RespuestasClientes() {
  const { usuario, tenant } = useAuth();

  // Esta sección es demasiado sensible (mensajes privados de los clientes)
  // para depender del sistema general de permisos por sección, que por
  // ahora no distingue nada más fino que "administrador o no" — así que se
  // restringe directamente a ADMIN, tanto aquí como en el servidor. El
  // check se aplica más abajo, DESPUÉS de declarar los hooks (nunca antes:
  // React exige que los hooks se llamen siempre en el mismo orden en cada
  // render, así que un "return" temprano antes de un hook rompería eso).
  const esAdmin = usuario?.rol === "ADMIN";

  const [conversaciones, setConversaciones] = useState<Conversacion[]>([]);
  const [busqueda, setBusqueda] = useState("");
  const [seleccionadaId, setSeleccionadaId] = useState<string | null>(null);
  const [cargandoLista, setCargandoLista] = useState(true);

  async function cargarLista() {
    try {
      const res = await api.get("/whatsapp-clientes/conversaciones");
      setConversaciones(res.data.items);
    } catch (err) {
      console.error(err);
    } finally {
      setCargandoLista(false);
    }
  }

  useEffect(() => {
    if (!esAdmin) return;
    cargarLista();
    const id = setInterval(cargarLista, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esAdmin]);

  if (!esAdmin) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-slate-500">
        <ShieldOff size={40} className="text-slate-300" />
        <p className="font-medium">Solo el administrador del negocio puede ver esta sección</p>
      </div>
    );
  }

  const filtradas = conversaciones.filter((c) => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return true;
    return c.telefono.includes(q) || (c.nombrePerfil ?? "").toLowerCase().includes(q);
  });

  const sinLeer = conversaciones.filter((c) => c.sinLeer).length;

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col">
      <div className="flex items-start gap-2.5 border-b border-amber-100 bg-amber-50 px-5 py-3 text-sm text-amber-800">
        <ShieldAlert size={18} className="mt-0.5 shrink-0" />
        <p>
          Aquí solo ves las respuestas de <strong>tus propios clientes</strong>. Para darles seguimiento,
          contéstales siempre desde <strong>tu WhatsApp de negocio</strong> (usa el botón "Responder por WhatsApp"
          de cada conversación) — nunca se manda nada desde aquí a nombre de Mascotas Vet. Por conversación solo se
          muestran los últimos 5 mensajes que enviaste y los últimos 5 que recibiste.
        </p>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Lista de conversaciones */}
        <aside className="flex w-full shrink-0 flex-col border-r border-slate-100 bg-white sm:w-80">
          <div className="border-b border-slate-100 p-3">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="input pl-8"
                placeholder="Buscar por nombre o número"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
            {sinLeer > 0 && (
              <p className="mt-2 text-xs font-medium text-teal-700">
                {sinLeer} {sinLeer === 1 ? "mensaje sin leer" : "mensajes sin leer"}
              </p>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {cargandoLista ? (
              <div className="flex justify-center py-10"><Spinner /></div>
            ) : filtradas.length === 0 ? (
              <p className="p-6 text-center text-sm text-slate-400">
                {conversaciones.length === 0
                  ? "Todavía no tienes respuestas de clientes aquí."
                  : "Sin resultados."}
              </p>
            ) : (
              filtradas.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSeleccionadaId(c.id)}
                  className={`flex w-full items-start gap-3 border-b border-slate-50 p-3 text-left hover:bg-slate-50 ${seleccionadaId === c.id ? "bg-teal-50" : ""}`}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold uppercase text-slate-600">
                    {(c.nombrePerfil?.[0] ?? c.telefono[0]) || "?"}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-slate-700">{c.nombrePerfil || c.telefono}</span>
                      {c.ultimoMensajeEn && <span className="shrink-0 text-[11px] text-slate-400">{fechaCorta(c.ultimoMensajeEn)}</span>}
                    </span>
                    <span className="flex items-center justify-between gap-2">
                      <span className={`truncate text-xs ${c.sinLeer ? "font-semibold text-slate-600" : "text-slate-400"}`}>{c.ultimoMensaje || "—"}</span>
                      {c.sinLeer && <span className="h-2 w-2 shrink-0 rounded-full bg-teal-500" />}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Hilo de mensajes */}
        <main className="hidden flex-1 flex-col bg-slate-50 sm:flex">
          {seleccionadaId ? (
            <Hilo
              conversacionId={seleccionadaId}
              conversacionInfo={conversaciones.find((c) => c.id === seleccionadaId)}
              tenantNombre={tenant?.nombre ?? "tu negocio"}
              onLeido={cargarLista}
            />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-slate-400">
              <MessageCircle size={36} />
              <p className="text-sm">Elige una conversación para ver los mensajes.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function Hilo({
  conversacionId,
  conversacionInfo,
  tenantNombre,
  onLeido,
}: {
  conversacionId: string;
  conversacionInfo?: Conversacion;
  tenantNombre: string;
  onLeido: () => void;
}) {
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  async function cargar() {
    try {
      const res = await api.get(`/whatsapp-clientes/conversaciones/${conversacionId}/mensajes`);
      setMensajes(res.data.mensajes);
      setError("");
      // Abrir el hilo marca la conversación como leída en el servidor —
      // se refresca la lista para que el punto de "sin leer" desaparezca
      // de inmediato en vez de esperar a la siguiente actualización
      // automática (cada 10s).
      onLeido();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    setCargando(true);
    cargar();
    const id = setInterval(cargar, 6000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversacionId]);

  const telefono = conversacionInfo?.telefono ?? "";
  const nombreCliente = conversacionInfo?.nombrePerfil || "";
  const saludo = `Hola${nombreCliente ? ` ${nombreCliente}` : ""}, soy ${tenantNombre}. Vi tu mensaje, te contacto por aquí para darte seguimiento.`;
  const linkWhatsapp = `https://wa.me/${telefono}?text=${encodeURIComponent(saludo)}`;

  return (
    <>
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3">
        <div>
          <div className="flex items-center gap-2 font-semibold text-slate-700">
            <User size={16} className="text-slate-400" />
            {conversacionInfo?.nombrePerfil || conversacionInfo?.telefono}
          </div>
          {conversacionInfo?.nombrePerfil && <div className="text-xs text-slate-400">{conversacionInfo.telefono}</div>}
        </div>
        <a
          href={linkWhatsapp}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-sm"
        >
          Responder por WhatsApp <ExternalLink size={14} />
        </a>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {cargando ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : error ? (
          <p className="text-center text-sm text-red-500">{error}</p>
        ) : (
          <div className="space-y-3">
            {mensajes.map((m) => (
              <Burbuja key={m.id} mensaje={m} conversacionId={conversacionId} />
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 bg-white px-5 py-3 text-center text-xs text-slate-400">
        Para contestarle a {conversacionInfo?.nombrePerfil || "tu cliente"}, usa el botón de arriba — se abre tu
        propio WhatsApp, no el de Mascotas Vet.
      </div>
    </>
  );
}

function Burbuja({ mensaje, conversacionId }: { mensaje: Mensaje; conversacionId: string }) {
  const propio = mensaje.direccion === "SALIENTE";
  return (
    <div className={`flex ${propio ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm sm:max-w-[70%] ${propio ? "bg-teal-500 text-white" : "bg-white text-slate-700 shadow-sm"}`}>
        <ContenidoMensaje mensaje={mensaje} conversacionId={conversacionId} propio={propio} />
        <div className={`mt-1 text-right text-[10px] ${propio ? "text-teal-100" : "text-slate-400"}`}>{horaCorta(mensaje.createdAt)}</div>
      </div>
    </div>
  );
}

function ContenidoMensaje({ mensaje, conversacionId, propio }: { mensaje: Mensaje; conversacionId: string; propio: boolean }) {
  const [urlMedia, setUrlMedia] = useState<string | null>(null);
  const [cargandoMedia, setCargandoMedia] = useState(false);

  async function cargarMedia(): Promise<string | null> {
    if (urlMedia) return urlMedia;
    if (cargandoMedia || !mensaje.mediaId) return null;
    setCargandoMedia(true);
    try {
      const res = await api.get(`/whatsapp-clientes/conversaciones/${conversacionId}/media/${mensaje.id}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      setUrlMedia(url);
      return url;
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setCargandoMedia(false);
    }
  }

  useEffect(() => {
    if (mensaje.tipo === "IMAGEN") cargarMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mensaje.id]);

  if (mensaje.tipo === "TEXTO" || mensaje.tipo === "OTRO") {
    return <p className="whitespace-pre-wrap">{mensaje.texto || "(mensaje sin texto)"}</p>;
  }

  if (mensaje.tipo === "UBICACION") {
    return (
      <p className="flex items-center gap-1.5">
        <MapPin size={14} /> {mensaje.texto}
      </p>
    );
  }

  if (mensaje.tipo === "IMAGEN") {
    return (
      <div>
        {cargandoMedia ? (
          <div className="flex h-32 w-40 items-center justify-center"><Spinner className="h-4 w-4" /></div>
        ) : urlMedia ? (
          <img src={urlMedia} alt="" className="max-h-64 rounded-lg" />
        ) : (
          <span className="text-xs opacity-70">No se pudo cargar la imagen</span>
        )}
        {mensaje.texto && <p className="mt-1">{mensaje.texto}</p>}
      </div>
    );
  }

  return (
    <button
      onClick={async () => {
        const url = await cargarMedia();
        if (url) window.open(url, "_blank");
      }}
      className={`flex items-center gap-2 text-sm ${propio ? "text-white" : "text-teal-600"}`}
    >
      {cargandoMedia ? <Spinner className="h-4 w-4" /> : <FileText size={16} />}
      {mensaje.texto || `Archivo (${mensaje.tipo.toLowerCase()})`}
      <Download size={14} />
    </button>
  );
}
